import { useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import ForbiddenPage from "../../components/ForbiddenPage";
import { i18n } from "../../translate/i18n";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PersonIcon from "@material-ui/icons/Person";
import StorageIcon from "@material-ui/icons/Storage";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import SpeedIcon from "@material-ui/icons/Speed";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import SecurityIcon from "@material-ui/icons/Security";
import BuildIcon from "@material-ui/icons/Build";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  backButton: {
    padding: "10px 20px",
    background: "#e8ecf1",
    color: "#1a1a1a",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    "&:hover": {
      background: "#d1d5db",
    },
  },
  templateCardsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "24px",
    marginBottom: "40px",
  },
  templateCard: {
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    border: "1px solid #e8ecf1",
    transition: "all 0.3s ease",
    display: "flex",
    flexDirection: "column",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
    },
  },
  templateCardContent: {
    padding: "24px",
    textAlign: "center",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  templateCardIcon: {
    fontSize: "48px",
    marginBottom: "16px",
    color: "#3b82f6",
  },
  templateCardTitle: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: "8px",
  },
  templateCardDescription: {
    fontSize: "13px",
    color: "#666",
    lineHeight: "1.5",
    marginBottom: "16px",
  },
  templateCardFeatures: {
    fontSize: "12px",
    color: "#999",
    textAlign: "left",
    background: "#f9fafb",
    padding: "12px",
    borderRadius: "6px",
    marginBottom: "16px",
    "& ul": {
      margin: 0,
      paddingLeft: "20px",
    },
    "& li": {
      marginBottom: "4px",
    },
  },
  templateCardActions: {
    padding: "16px 24px",
    borderTop: "1px solid #e8ecf1",
    display: "flex",
    gap: "8px",
    justifyContent: "center",
  },
  templateButton: {
    padding: "10px 20px",
    background: theme.palette.primary.main,
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    flex: 1,
    "&:hover": {
      background: theme.palette.primary.dark,
      transform: "translateY(-1px)",
      boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
    },
  },
}));

const Templates = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      try {
        if (!user || !user.companyId) return;
        const companyId = user.companyId;
        const planConfigs = await getPlanCompany(undefined, companyId);
        if (!planConfigs || !planConfigs.plan || !planConfigs.plan.useCampaigns) {
          toast.error(i18n.t("templates.noPermission"));
          setTimeout(() => {
            history.push(`/`)
          }, 500); // Reduzir delay de 1000ms para 500ms
        }
      } catch (err) {
        console.error('Erro ao verificar permissões:', err);
        toast.error(i18n.t("templates.permissionError"));
        setTimeout(() => {
          history.push(`/`)
        }, 500); // Reduzir delay de 1000ms para 500ms
      }
    }
    fetchData();
  }, [user, getPlanCompany, history]);

  const handleCreateCampaignFromTemplate = (templateType) => {
    // Navega para a página de configuração com o tipo de template pré-selecionado
    history.push(`/campaign-config?templateType=${templateType}`);
  };

  if (user.profile === "user" && user?.showCampaign === "disabled") {
    return <ForbiddenPage />;
  }

  return (
    <div className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.title}>
          <button
            onClick={() => history.push("/campaigns")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            <ArrowBackIcon style={{ fontSize: "28px", color: "#3b82f6" }} />
          </button>
          📋 {i18n.t("templates.title")}
        </div>
      </div>

      {/* Template Cards */}
      <div className={classes.templateCardsContainer}>
        {/* Card Humanizado */}
        <div className={classes.templateCard}>
          <div className={classes.templateCardContent}>
            <PersonIcon className={classes.templateCardIcon} />
            <div className={classes.templateCardTitle}>{i18n.t("templates.humanized.title")}</div>
            <div className={classes.templateCardDescription}>
              {i18n.t("templates.humanized.description")}
            </div>
            <div className={classes.templateCardFeatures}>
              <ul>
                <li>{i18n.t("templates.humanized.features.f1")}</li>
                <li>{i18n.t("templates.humanized.features.f2")}</li>
                <li>{i18n.t("templates.humanized.features.f3")}</li>
                <li>🔌 <strong>{i18n.t("templates.humanized.features.api")}</strong></li>
                <li>📊 {i18n.t("templates.humanized.features.contacts")}</li>
              </ul>
            </div>
          </div>
          <div className={classes.templateCardActions}>
            <button
              className={classes.templateButton}
              onClick={() => handleCreateCampaignFromTemplate("humanizado")}
            >
              {i18n.t("templates.useTemplate")}
            </button>
          </div>
        </div>

        {/* Card Bot */}
        <div className={classes.templateCard}>
          <div className={classes.templateCardContent}>
            <StorageIcon className={classes.templateCardIcon} />
            <div className={classes.templateCardTitle}>{i18n.t("templates.bot.title")}</div>
            <div className={classes.templateCardDescription}>
              {i18n.t("templates.bot.description")}
            </div>
            <div className={classes.templateCardFeatures}>
              <ul>
                <li>{i18n.t("templates.bot.features.f1")}</li>
                <li>{i18n.t("templates.bot.features.f2")}</li>
                <li>{i18n.t("templates.bot.features.f3")}</li>
                <li>🔌 <strong>{i18n.t("templates.bot.features.api")}</strong></li>
                <li>📊 {i18n.t("templates.bot.features.contacts")}</li>
              </ul>
            </div>
          </div>
          <div className={classes.templateCardActions}>
            <button
              className={classes.templateButton}
              onClick={() => handleCreateCampaignFromTemplate("bot")}
            >
              {i18n.t("templates.useTemplate")}
            </button>
          </div>
        </div>

        {/* Card Forçado */}
        <div className={classes.templateCard}>
          <div className={classes.templateCardContent}>
            <FlashOnIcon className={classes.templateCardIcon} />
            <div className={classes.templateCardTitle}>{i18n.t("templates.forced.title")}</div>
            <div className={classes.templateCardDescription}>
              {i18n.t("templates.forced.description")}
            </div>
            <div className={classes.templateCardFeatures}>
              <ul>
                <li>{i18n.t("templates.forced.features.f1")}</li>
                <li>{i18n.t("templates.forced.features.f2")}</li>
                <li>{i18n.t("templates.forced.features.f3")}</li>
                <li>🔌 <strong>{i18n.t("templates.forced.features.api")}</strong></li>
                <li>📊 {i18n.t("templates.forced.features.contacts")}</li>
              </ul>
            </div>
          </div>
          <div className={classes.templateCardActions}>
            <button
              className={classes.templateButton}
              onClick={() => handleCreateCampaignFromTemplate("forcado")}
            >
              {i18n.t("templates.useTemplate")}
            </button>
          </div>
        </div>

        {/* Card Velocidade */}
        <div className={classes.templateCard}>
          <div className={classes.templateCardContent}>
            <SpeedIcon className={classes.templateCardIcon} />
            <div className={classes.templateCardTitle}>{i18n.t("templates.speed.title")}</div>
            <div className={classes.templateCardDescription}>
              {i18n.t("templates.speed.description")}
            </div>
            <div className={classes.templateCardFeatures}>
              <ul>
                <li>{i18n.t("templates.speed.features.f1")}</li>
                <li>{i18n.t("templates.speed.features.f2")}</li>
                <li>{i18n.t("templates.speed.features.f3")}</li>
                <li>🔌 <strong>{i18n.t("templates.speed.features.api")}</strong></li>
                <li>📊 {i18n.t("templates.speed.features.contacts")}</li>
              </ul>
            </div>
          </div>
          <div className={classes.templateCardActions}>
            <button
              className={classes.templateButton}
              onClick={() => handleCreateCampaignFromTemplate("velocidade")}
            >
              {i18n.t("templates.useTemplate")}
            </button>
          </div>
        </div>

        {/* Card Crescimento */}
        <div className={classes.templateCard}>
          <div className={classes.templateCardContent}>
            <TrendingUpIcon className={classes.templateCardIcon} />
            <div className={classes.templateCardTitle}>{i18n.t("templates.growth.title")}</div>
            <div className={classes.templateCardDescription}>
              {i18n.t("templates.growth.description")}
            </div>
            <div className={classes.templateCardFeatures}>
              <ul>
                <li>{i18n.t("templates.growth.features.f1")}</li>
                <li>{i18n.t("templates.growth.features.f2")}</li>
                <li>{i18n.t("templates.growth.features.f3")}</li>
                <li>🔌 <strong>{i18n.t("templates.growth.features.api")}</strong></li>
                <li>📊 {i18n.t("templates.growth.features.contacts")}</li>
              </ul>
            </div>
          </div>
          <div className={classes.templateCardActions}>
            <button
              className={classes.templateButton}
              onClick={() => handleCreateCampaignFromTemplate("crescimento")}
            >
              {i18n.t("templates.useTemplate")}
            </button>
          </div>
        </div>

        {/* Card Segurança */}
        <div className={classes.templateCard}>
          <div className={classes.templateCardContent}>
            <SecurityIcon className={classes.templateCardIcon} />
            <div className={classes.templateCardTitle}>{i18n.t("templates.security.title")}</div>
            <div className={classes.templateCardDescription}>
              {i18n.t("templates.security.description")}
            </div>
            <div className={classes.templateCardFeatures}>
              <ul>
                <li>{i18n.t("templates.security.features.f1")}</li>
                <li>{i18n.t("templates.security.features.f2")}</li>
                <li>{i18n.t("templates.security.features.f3")}</li>
                <li>🔌 <strong>{i18n.t("templates.security.features.api")}</strong></li>
                <li>📊 {i18n.t("templates.security.features.contacts")}</li>
              </ul>
            </div>
          </div>
          <div className={classes.templateCardActions}>
            <button
              className={classes.templateButton}
              onClick={() => handleCreateCampaignFromTemplate("seguranca")}
            >
              {i18n.t("templates.useTemplate")}
            </button>
          </div>
        </div>

        {/* Card Customizado */}
        <div className={classes.templateCard}>
          <div className={classes.templateCardContent}>
            <BuildIcon className={classes.templateCardIcon} />
            <div className={classes.templateCardTitle}>{i18n.t("templates.custom.title")}</div>
            <div className={classes.templateCardDescription}>
              {i18n.t("templates.custom.description")}
            </div>
            <div className={classes.templateCardFeatures}>
              <ul>
                <li>{i18n.t("templates.custom.features.f1")}</li>
                <li>{i18n.t("templates.custom.features.f2")}</li>
                <li>{i18n.t("templates.custom.features.f3")}</li>
                <li>🔌 <strong>{i18n.t("templates.custom.features.api")}</strong></li>
                <li>📊 {i18n.t("templates.custom.features.contacts")}</li>
              </ul>
            </div>
          </div>
          <div className={classes.templateCardActions}>
            <button
              className={classes.templateButton}
              onClick={() => history.push("/campaigns")}
            >
              {i18n.t("templates.createFromScratch")}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Templates;
